import { useState, useEffect } from "react";
import { Search, RefreshCw, Calendar, FileVideo, CheckCircle, XCircle, Loader2, Clock } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { zhCN } from "date-fns/locale";

interface UploadLog {
  id: string;
  video_id: string | null;
  uploaded_by: string;
  created_at: string;
  file_name: string;
  file_size: number;
  file_type: string | null;
  status: string;
  duration_ms: number | null;
  cos_key: string | null;
  cdn_url: string | null;
  subtitle_count: number | null;
  subtitle_source: string | null;
  error_message: string | null;
  uploader_name?: string;
  video_title?: string;
  metadata?: {
    detailed_log?: string;
    title?: string;
  };
}

export default function UploadLogs() {
  const [logs, setLogs] = useState<UploadLog[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [selectedLog, setSelectedLog] = useState<UploadLog | null>(null);
  const [showLogDialog, setShowLogDialog] = useState(false);

  const fetchLogs = async () => {
    setIsLoading(true);

    const { data: logsData, error } = await supabase
      .from("upload_logs")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(100);

    if (error) {
      console.error("Error fetching upload logs:", error);
      setIsLoading(false);
      return;
    }

    const safeLogs = (logsData || []) as unknown as UploadLog[];

    // Get uploader names
    const uploaderIds = [
      ...new Set(
        safeLogs
          .map((l) => (typeof l.uploaded_by === "string" ? l.uploaded_by : ""))
          .filter(Boolean),
      ),
    ];
    let profilesData: Array<{ user_id: string; nickname: string | null }> = [];
    if (uploaderIds.length > 0) {
      const { data, error: profilesError } = await supabase
        .from("profiles")
        .select("user_id, nickname")
        .in("user_id", uploaderIds);
      if (profilesError) {
        console.error("Error fetching profiles:", profilesError);
      } else {
        profilesData = (data || []) as Array<{ user_id: string; nickname: string | null }>;
      }
    }
    const profilesMap = new Map(profilesData.map((p) => [p.user_id, p.nickname]));

    // Get video titles
    const videoIds = [
      ...new Set(
        safeLogs
          .map((l) => (typeof l.video_id === "string" ? l.video_id : ""))
          .filter(Boolean),
      ),
    ];
    let videosData: Array<{ id: string; title: string }> = [];
    if (videoIds.length > 0) {
      const { data, error: videosError } = await supabase
        .from("videos")
        .select("id, title")
        .in("id", videoIds);
      if (videosError) {
        console.error("Error fetching videos:", videosError);
      } else {
        videosData = (data || []) as Array<{ id: string; title: string }>;
      }
    }
    const videosMap = new Map(videosData.map((v) => [v.id, v.title]));

    const enrichedLogs = safeLogs.map((log) => {
      const uploadedBy = typeof log.uploaded_by === "string" ? log.uploaded_by : "";
      const videoId = typeof log.video_id === "string" ? log.video_id : null;
      const fileName = typeof log.file_name === "string" ? log.file_name : "";
      return {
        ...log,
        file_name: fileName,
        uploader_name: profilesMap.get(uploadedBy) || (uploadedBy ? uploadedBy.slice(0, 8) : "unknown"),
        video_title: videoId ? videosMap.get(videoId) : undefined,
      };
    });

    setLogs(enrichedLogs);
    setIsLoading(false);
  };

  useEffect(() => {
    fetchLogs();
  }, []);

  const filteredLogs = logs.filter((log) => {
    const fileName = typeof log.file_name === "string" ? log.file_name : "";
    const uploaderName = typeof log.uploader_name === "string" ? log.uploader_name : "";
    const videoTitle = typeof log.video_title === "string" ? log.video_title : "";
    const matchesSearch =
      fileName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      uploaderName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      videoTitle.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesStatus = statusFilter === "all" || log.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const formatFileSize = (bytes: number) => {
    if (bytes >= 1073741824) return (bytes / 1073741824).toFixed(2) + " GB";
    if (bytes >= 1048576) return (bytes / 1048576).toFixed(2) + " MB";
    if (bytes >= 1024) return (bytes / 1024).toFixed(2) + " KB";
    return bytes + " B";
  };

  const formatDuration = (ms: number | null) => {
    if (!ms) return "-";
    if (ms >= 60000) return (ms / 60000).toFixed(1) + " 分钟";
    if (ms >= 1000) return (ms / 1000).toFixed(1) + " 秒";
    return ms + " 毫秒";
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "completed":
        return (
          <Badge variant="outline" className="border-emerald-500/30 text-emerald-600 gap-1">
            <CheckCircle className="h-3 w-3" />
            完成
          </Badge>
        );
      case "failed":
        return (
          <Badge variant="outline" className="border-destructive/30 text-destructive gap-1">
            <XCircle className="h-3 w-3" />
            失败
          </Badge>
        );
      case "processing":
        return (
          <Badge variant="outline" className="border-primary/30 text-primary gap-1">
            <Loader2 className="h-3 w-3 animate-spin" />
            处理中
          </Badge>
        );
      case "uploading":
        return (
          <Badge variant="outline" className="border-muted-foreground/30 text-muted-foreground gap-1">
            <Clock className="h-3 w-3" />
            上传中
          </Badge>
        );
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const stats = {
    total: logs.length,
    completed: logs.filter((l) => l.status === "completed").length,
    failed: logs.filter((l) => l.status === "failed").length,
    processing: logs.filter((l) => l.status === "processing" || l.status === "uploading").length,
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">上传日志</h1>
          <p className="text-muted-foreground">追踪视频上传和处理状态</p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchLogs}>
          <RefreshCw className="h-4 w-4 mr-2" />
          刷新
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold">{stats.total}</div>
            <div className="text-sm text-muted-foreground">总上传</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-emerald-600">{stats.completed}</div>
            <div className="text-sm text-muted-foreground">成功</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-destructive">{stats.failed}</div>
            <div className="text-sm text-muted-foreground">失败</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-amber-600">{stats.processing}</div>
            <div className="text-sm text-muted-foreground">进行中</div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="搜索文件名、上传者..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-32">
                <SelectValue placeholder="状态" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">全部</SelectItem>
                <SelectItem value="completed">完成</SelectItem>
                <SelectItem value="failed">失败</SelectItem>
                <SelectItem value="processing">处理中</SelectItem>
                <SelectItem value="uploading">上传中</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-8 text-muted-foreground">加载中...</div>
          ) : filteredLogs.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">暂无上传记录</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>文件</TableHead>
                  <TableHead>大小</TableHead>
                  <TableHead>状态</TableHead>
                  <TableHead>字幕</TableHead>
                  <TableHead>耗时</TableHead>
                  <TableHead>上传者</TableHead>
                  <TableHead>时间</TableHead>
                  <TableHead>日志</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredLogs.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <FileVideo className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                        <div className="min-w-0">
                          <div className="font-medium truncate max-w-[200px]">{log.file_name}</div>
                          {log.video_title && (
                            <div className="text-xs text-muted-foreground truncate max-w-[200px]">
                              → {log.video_title}
                            </div>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatFileSize(log.file_size)}
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        {getStatusBadge(log.status)}
                        {log.error_message && (
                          <div className="text-xs text-destructive max-w-[150px] truncate" title={log.error_message}>
                            {log.error_message}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {log.subtitle_count != null ? (
                        <div className="text-sm">
                          <span className="font-medium">{log.subtitle_count}</span>
                          <span className="text-muted-foreground text-xs ml-1">
                            ({log.subtitle_source === "vtt" ? "VTT" : "AI"})
                          </span>
                        </div>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatDuration(log.duration_ms)}
                    </TableCell>
                    <TableCell className="text-muted-foreground">{log.uploader_name}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-sm text-muted-foreground">
                        <Calendar className="h-3 w-3" />
                        {format(new Date(log.created_at), "M月d日 HH:mm", { locale: zhCN })}
                      </div>
                    </TableCell>
                    <TableCell>
                      {log.metadata?.detailed_log && (
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => {
                            setSelectedLog(log);
                            setShowLogDialog(true);
                          }}
                        >
                          查看
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={showLogDialog} onOpenChange={setShowLogDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>流水线日志 - {selectedLog?.file_name}</DialogTitle>
          </DialogHeader>
          <ScrollArea className="h-[60vh] w-full rounded-md border p-4 bg-muted/50 font-mono text-xs">
            {selectedLog?.metadata?.detailed_log?.split('\n').map((line, i) => (
              <div key={i} className="mb-1 whitespace-pre-wrap break-all">
                {line}
              </div>
            )) || "无日志详情"}
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
  );
}
